import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyGetEventsExportQuery } from '@store/api/eventService/eventServiceApi';
import { useGlobalToast } from '@/hooks/useGlobalToast';
import { downloadBlob } from '@/utils/downloadBlob';
import { eventFiltersToParams, hasAnyFilter } from '@/utils/eventFiltersToParams';
import { getLocalizationConfig } from '@config/localizationConfig';
import type { EventFilters } from '@/types/planner.types';
import type { GetEventsParams } from '@store/models';

export interface UseExportEventsOptions {
  filters?: EventFilters;
  expectLong?: boolean;
}

export interface UseExportEventsResult {
  exportEvents: () => Promise<void>;
  isExporting: boolean;
}

export function useExportEvents(options: UseExportEventsOptions = {}): UseExportEventsResult {
  const { filters, expectLong } = options;
  const { t } = useTranslation('planner');
  const { showToast } = useGlobalToast();
  const [trigger] = useLazyGetEventsExportQuery();
  const [isExporting, setIsExporting] = useState(false);
  const inFlightRef = useRef(false);

  const exportEvents = useCallback(async () => {
    if (inFlightRef.current) return;
    const hasFilters = hasAnyFilter(filters);
    const filterParams = hasFilters ? eventFiltersToParams(filters as EventFilters) : undefined;
    const dateFormat = getLocalizationConfig().dateFormatPattern;
    const params: GetEventsParams = { ...(filterParams ?? {}), dateFormat };
    const isLongPath = expectLong === true || !hasFilters;

    inFlightRef.current = true;
    setIsExporting(true);
    try {
      if (isLongPath) {
        showToast({ message: t('dialogs.toast.exportStartedLong'), intent: 'info' });
      }
      const result = await trigger(params).unwrap();
      downloadBlob(result.blob, result.filename);
    } catch {
      showToast({ message: t('dialogs.toast.exportFailed'), intent: 'error' });
    } finally {
      inFlightRef.current = false;
      setIsExporting(false);
    }
  }, [filters, expectLong, trigger, showToast, t]);

  return { exportEvents, isExporting };
}
